import { useEffect, useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { useAtomValue, useSetAtom } from 'jotai';

import type { LatestImage } from '@/@types/latestImages';
import type { ImageAnalyses } from '@/@types/latestImagesV2';
import { PerioChart } from '@/common-components/composed/PerioChart/PerioChart';
import { generateFmxGrid } from '@/modules/claims/cleanClaims/helpers';
import { claimAttachmentIdAtom } from '@/modules/claims/freeClaims/atoms';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';
import {
  claimAttachmentToothLevelImagesAtom,
  cleanClaimsAttachmentValuesAtom,
  clinicalNotesAtom,
  includeClinicalNoteInAttachmentAtom,
  narrativeAtom,
  selectedTreatmentsAtom,
  showAnnotationsAtom,
  showPerioChartAtom,
  useLocalizedFormatDate,
} from '@/modules/claims/freeClaims/state';
import { useSaveAttachmentAudit } from '@/react-query/rcm/useSaveAttachmentAudit';
import { usePreferences } from '@/react-query/usePreferences';

import { ClaimFooter } from './ClaimFooter';
import { ClaimHeader } from './ClaimHeader';
import { ClaimNarrative } from './ClaimNarrative';
import { QuadLevelImagesGroup } from './insuranceImagesSection/QuadLevelImagesGroup';
import { ToothLevelImagesGroup } from './insuranceImagesSection/ToothLevelImagesGroup';

export type TTreatmentDisplay = { cdtCode?: string; quadrant?: string };
export type GroupedTeethImages = LatestImage[];

type ClaimAttachmentContentProps = {
  claimSummary?: GetClaimSummaryResponse;
  filteredColorImages?: ImageAnalyses[];
  filteredRadiographs?: ImageAnalyses[];
  hasToothLevelSummary: boolean;
  hasQuadLevelSummary: boolean;
  isLoadingClaimSummary: boolean;
};

export const ClaimAttachmentContent = ({
  claimSummary,
  filteredColorImages,
  filteredRadiographs,
  hasToothLevelSummary,
  hasQuadLevelSummary,
  isLoadingClaimSummary,
}: ClaimAttachmentContentProps) => {
  // atoms
  const selectedTreatmentIds = useAtomValue(selectedTreatmentsAtom);
  const narrativeText = useAtomValue(narrativeAtom);
  const clinicalNotes = useAtomValue(clinicalNotesAtom);
  const includeClinicalNoteInAttachment = useAtomValue(
    includeClinicalNoteInAttachmentAtom,
  );
  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );
  const toothLevelImages = useAtomValue(claimAttachmentToothLevelImagesAtom);

  const setClaimAttachmentId = useSetAtom(claimAttachmentIdAtom);

  const showAnnotations = useAtomValue(showAnnotationsAtom);
  const showPerioChart = useAtomValue(showPerioChartAtom);

  // hooks
  const { data: prefs } = usePreferences();
  const { mutate: saveAuditEntry } = useSaveAttachmentAudit();
  const formatDate = useLocalizedFormatDate();

  // Check if tooth level images have annotations/table data
  const hasToothLevelAnnotations = useMemo(() => {
    const hasAnnotations = (annotations: any) => {
      if (!annotations) return false;
      return Object.values(annotations).some((arr: any) => arr?.length > 0);
    };

    // Check if any tooth level image has annotations or table data (which indicates actual findings)
    return toothLevelImages.some(
      (image) =>
        hasAnnotations(image.annotations) || image.tableData?.length > 0,
    );
  }, [toothLevelImages]);

  // variables
  const fmxGrid = useMemo(
    () => generateFmxGrid(claimSummary, selectedTreatmentIds ?? []),
    [claimSummary, selectedTreatmentIds],
  );

  const filteredQuadLevelImages = useMemo(
    () => fmxGrid.filter((img) => img !== null) as ImageAnalyses[],
    [fmxGrid],
  );

  const formattedPerioChartDate = claimSummary?.perioChart?.examDate
    ? formatDate(claimSummary.perioChart.examDate)
    : '';

  const { estimatedVisitId, unifiedId } = cleanClaimsAttachmentValues;

  const showNarrative = prefs?.cleanClaims;

  // useEffects
  // save audit entry and generate claim attachment id
  useEffect(() => {
    if (
      !isLoadingClaimSummary &&
      (hasToothLevelSummary || hasQuadLevelSummary)
    ) {
      const imageIds = filteredQuadLevelImages
        ?.concat(filteredRadiographs ?? [])
        .concat(filteredColorImages ?? [])
        .map((i) => i.imageId);
      saveAuditEntry(
        {
          unifiedAppointmentId: unifiedId ? parseInt(unifiedId, 10) : undefined,
          estimatedVisitId: estimatedVisitId
            ? parseInt(estimatedVisitId, 10)
            : undefined,
          imageIds: imageIds,
          narrative: narrativeText,
          view: 'preview-single',
          options: {
            includeAllImages: false,
            bitewingsOnly: false,
            includePrlFindings: false,
            includeCariesFindings: hasToothLevelSummary,
            includeDmfFindings: hasToothLevelSummary,
            includeRblAndCalculusFindings: hasQuadLevelSummary,
          },
        },
        {
          onSuccess: (data: { data: { attachmentId: string } }) => {
            if (data?.data?.attachmentId) {
              setClaimAttachmentId(data.data.attachmentId);
            }
          },
        },
      );
    }
  }, [
    narrativeText,
    unifiedId,
    estimatedVisitId,
    filteredColorImages,
    filteredRadiographs,
    filteredQuadLevelImages,
    isLoadingClaimSummary,
    hasToothLevelSummary,
    hasQuadLevelSummary,
    saveAuditEntry,
    setClaimAttachmentId,
  ]);

  return (
    <div
      data-testid="claim-attachment-content"
      className="vai-max-w-[1594px] vai-h-fit vai-p-8 vai-bg-white vai-border"
    >
      <h2 data-testid="preview-title" className="vai-text-xl vai-font-bold">
        <Trans>Claim Attachment</Trans>
      </h2>
      <div
        data-testid="preview-content"
        className="vai-flex vai-flex-col vai-gap-6 vai-mt-6"
      >
        <ClaimHeader />
        <div
          data-testid="preview-sections"
          className="vai-flex vai-flex-col vai-gap-8"
        >
          {showNarrative && <ClaimNarrative />}
          {includeClinicalNoteInAttachment && clinicalNotes && (
            <div>
              <h2
                className="vai-font-bold vai-text-lg"
                data-testid="clinical-note-title"
              >
                <Trans>Clinical Note</Trans>
              </h2>
              <div
                data-testid="clinical-notes"
                className="vai-text-sm vai-whitespace-pre-wrap"
              >
                {clinicalNotes}
              </div>
            </div>
          )}
          {hasToothLevelSummary && (
            <ToothLevelImagesGroup
              filteredRadiographs={filteredRadiographs ?? []}
              filteredColorImages={filteredColorImages ?? []}
              claimSummary={claimSummary}
            />
          )}
          {hasQuadLevelSummary && (
            <QuadLevelImagesGroup
              fmxGrid={fmxGrid}
              filteredImages={filteredQuadLevelImages}
              claimSummary={claimSummary}
              showAnnotations={showAnnotations}
              showTable
              showOriginal
            />
          )}
          {!!claimSummary?.perioChart?.teeth?.length && showPerioChart && (
            <div className="vai-w-full vai-overflow-x-auto">
              <div className="vai-min-w-[1200px] vai-border-2 vai-border-slate-200 vai-rounded-lg">
                <div className="vai-text-left vai-w-full vai-border-b vai-border-slate-200 vai-bg-slate-100 vai-p-2 vai-text-sm vai-font-semibold vai-rounded-t-lg">
                  <h1 className="vai-text-lg vai-font-bold">
                    <Trans>Perio Chart</Trans>
                  </h1>
                  {formattedPerioChartDate && (
                    <span className="vai-text-slate-500 vai-text-sm vai-font-normal">
                      {formattedPerioChartDate}
                    </span>
                  )}
                </div>
                <PerioChart
                  mode="light"
                  perioChartData={claimSummary.perioChart}
                />
              </div>
            </div>
          )}
          <ClaimFooter
            showClinicalValuesExplanation={hasQuadLevelSummary}
            showDmfExplanation={hasToothLevelAnnotations}
            showRestoExplanation={hasToothLevelAnnotations}
            showRblExplanation={hasQuadLevelSummary}
          />
        </div>
      </div>
    </div>
  );
};
